import { Button } from "../ui/button";
import { focusedControlOpenState } from "../stage/focused-overlay-styles";
import { Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverTitle,
  PopoverDescription,
} from "../ui/popover";
import { cn } from "../../lib/utils";
import { initials } from "../../room/initials";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import type { Spectator } from "../../room/state/participants";

function SpectatorList({ spectators }: { spectators: Spectator[] }) {
  const { t } = useTranslation();
  if (!spectators.length)
    return <PopoverDescription className="mt-4">{t("focused.noSpectators")}</PopoverDescription>;
  return (
    <ul className="mt-4 space-y-3">
      {spectators.map((spectator) => (
        <li key={spectator.id} className="flex min-w-0 items-center gap-2">
          <Avatar>
            <AvatarImage
              src={spectator.picture || undefined}
              alt=""
              referrerPolicy="no-referrer"
              className="object-cover"
            />
            <AvatarFallback>{initials(spectator.name)}</AvatarFallback>
          </Avatar>
          <span className="truncate text-sm">
            {spectator.self ? t("common.meSuffix", { name: spectator.name }) : spectator.name}
          </span>
        </li>
      ))}
    </ul>
  );
}

// The viewer control sits in the focused-stream overlay and opens below; the
// host control sits above the screen share pill and opens above it.
export function SpectatorsControl({
  kind,
  className = "",
  buttonClassName = "",
  arrowClassName,
}: {
  kind: "viewer" | "host";
  className?: string;
  buttonClassName?: string;
  arrowClassName?: string;
}) {
  const { t } = useTranslation();
  const { setFocusedPopoverOpen } = useRoomController();
  const spectators = useRoomState((state) =>
    kind === "viewer" ? state.participants.viewerSpectators : state.participants.hostSpectators,
  );
  const count = spectators?.length ?? 0;
  const label = t("common.spectator", { count });
  const content = (
    <>
      <Eye aria-hidden="true" />
      <span>{count}</span>
    </>
  );

  return (
    <div
      id={kind === "viewer" ? "spectatorsWrap" : "hostSpectatorsWrap"}
      data-focused-overlay-use={kind === "viewer" ? "" : undefined}
      className={cn("spectators-control relative", className)}
      hidden={!spectators}
    >
      <Popover
        onOpenChange={(open) => {
          if (kind === "viewer") setFocusedPopoverOpen("spectators", open);
        }}
      >
        <PopoverTrigger
          asChild={kind === "viewer"}
          className={
            kind === "host"
              ? cn(
                  "spectators-action flex w-auto items-center justify-center gap-1 px-2.5 text-xs font-bold [&_svg]:size-3.5",
                  buttonClassName,
                )
              : undefined
          }
          aria-label={label}
        >
          {kind === "viewer" ? (
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "spectators-action text-muted-foreground",
                focusedControlOpenState,
                buttonClassName,
              )}
            >
              {content}
            </Button>
          ) : (
            <>
              {content}
              {arrowClassName ? <span aria-hidden="true" className={arrowClassName} /> : null}
            </>
          )}
        </PopoverTrigger>
        <PopoverContent
          side={kind === "viewer" ? "bottom" : "top"}
          align="start"
          collisionPadding={8}
          sticky="always"
          // Offsets match the old absolute placement relative to the wrapper.
          sideOffset={kind === "viewer" ? 12 : 8}
          alignOffset={kind === "viewer" ? -4 : -42}
          aria-labelledby={kind === "viewer" ? "spectatorsTitle" : "hostSpectatorsTitle"}
          className="max-h-(--radix-popover-content-available-height) w-64 max-w-[calc(100vw-1rem)] overflow-x-hidden overflow-y-auto"
        >
          <PopoverTitle id={kind === "viewer" ? "spectatorsTitle" : "hostSpectatorsTitle"}>
            <h2>{t("focused.spectators")}</h2>
          </PopoverTitle>
          {spectators ? <SpectatorList spectators={spectators} /> : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
